import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-common/requests/search';
import type { OpenSearchResponse } from 'tio-common/models/search';
import {
  type RecentApplication,
  ALL_APPLICATION_STATES,
  ALL_PROGRAM_INSTANCE_STATES,
  buildType4Instances,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';

export interface BenefitHistoryModel {
  applications: RecentApplication[];
  programInstances: Array<{
    instanceApplication: RecentApplication;
    childApplications: RecentApplication[];
  }>;
  availableYears: number[];
  currentYear: number;
  limitsStatusCurrent?: LimitsStatusDataSignature;
  limitsStatusPrevious?: LimitsStatusDataSignature;
  limitsStatusNext?: LimitsStatusDataSignature;
}

export default class AuthenticatedTasBenefitHistoryRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  async model(): Promise<BenefitHistoryModel> {
    const currentEmployee = this.sessionContext.currentEmployee;

    // Validate employee exists and has required relationships
    if (!currentEmployee?.id) {
      console.error('No current employee found in session context');
      throw new Error('Employee information not loaded. Please refresh the page.');
    }

    const companyId = currentEmployee.company?.id;
    if (!companyId) {
      console.error('Company ID not available on currentEmployee', {
        employeeId: currentEmployee.id,
        company: currentEmployee.company,
      });
      throw new Error('Company information not loaded. Please refresh the page.');
    }

    const { flatApplications, programInstances, availableYears } = await this.fetchBenefitHistory(
      currentEmployee.id,
      companyId
    );

    const limitsStatusCurrent = await this.tuitionAssistance.getV2LimitsStatus(
      currentEmployee,
      undefined,
      false
    );

    let limitsStatusPrevious: LimitsStatusDataSignature | undefined;
    let limitsStatusNext: LimitsStatusDataSignature | undefined;

    if (limitsStatusCurrent?.annual_start_date) {
      const currentStart = new Date(limitsStatusCurrent.annual_start_date);
      const previousDate = new Date(currentStart);
      previousDate.setFullYear(currentStart.getFullYear() - 1);

      const nextDate = new Date(currentStart);
      nextDate.setFullYear(currentStart.getFullYear() + 1);

      const previousDateParam = previousDate.toISOString().slice(0, 10);
      const nextDateParam = nextDate.toISOString().slice(0, 10);

      [limitsStatusPrevious, limitsStatusNext] = await Promise.all([
        this.tuitionAssistance.getV2LimitsStatus(
          currentEmployee,
          undefined,
          false,
          previousDateParam
        ),
        this.tuitionAssistance.getV2LimitsStatus(currentEmployee, undefined, false, nextDateParam),
      ]);
    }

    const currentYear = new Date().getFullYear();

    return {
      applications: flatApplications,
      programInstances,
      availableYears: availableYears.sort((a, b) => b - a), // Descending
      currentYear,
      limitsStatusCurrent,
      limitsStatusPrevious,
      limitsStatusNext,
    };
  }

  /**
   * Fetch all benefit history (no date restriction like dashboard)
   * Returns applications and instances grouped by year
   */
  async fetchBenefitHistory(
    employeeId: string,
    companyId: string
  ): Promise<{
    flatApplications: RecentApplication[];
    programInstances: Array<{
      instanceApplication: RecentApplication;
      childApplications: RecentApplication[];
    }>;
    availableYears: number[];
  }> {
    try {
      // Query ALL applications (no date restriction)
      const builder = new OpenSearchQueryBuilder()
        .mustTerm('company_id', companyId)
        .mustTerm('employee_id', employeeId)
        .addFilter({
          bool: {
            should: [
              { terms: { tas_application_state_keyword: ALL_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ALL_PROGRAM_INSTANCE_STATES } },
            ],
            minimum_should_match: 1,
          },
        })
        .setSort('updated_at:desc')
        .setPagination(1, 500); // Increased limit for history

      const query = {
        'model-type': 'TasProgramInstance',
        searchBody: builder.build(),
      };

      const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
        content: OpenSearchResponse<RecentApplication>;
      };
      const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
      const rawDocs = responseBuilder.getDocuments();
      const allApps = normalizePaymentFields(rawDocs);

      // Separate Type 4 and group by instance
      const type4InstanceMap = new Map<string, RecentApplication[]>();
      const flatApps: RecentApplication[] = [];
      const yearsSet = new Set<number>();

      for (const app of allApps) {
        // Extract year from TAS-specific date fields for academic year filtering
        const dateStr =
          app.tasProgramInstanceCreatedAtDate ||
          app.tasApplicationCreatedAtDate ||
          app.documentCreatedAt ||
          app.createdAt;
        if (dateStr) {
          const year = new Date(dateStr).getFullYear();
          yearsSet.add(year);
        }

        const type = app.tasProgramTemplateTypeKeyword;
        const isInstanceRecord = app.recordType === 'instance';

        // Type 1: Program instance with no children - treat as instance
        if (type === 'TAS.ProgramType.1') {
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        }
        // Type 4.2/4.3 templates: Group instances with their applications
        else if (
          (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') &&
          isInstanceRecord
        ) {
          // This is a Type 4 instance record
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        } else if (
          (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') &&
          !isInstanceRecord
        ) {
          // This is a Type 4.2/4.3 application record
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        } else {
          // Type 2, 3 applications
          flatApps.push(app);
        }
      }

      // Build Type 1 and Type 4.2/4.3 instances with sorted child applications
      const programInstances = buildType4Instances(type4InstanceMap);
      const availableYears = Array.from(yearsSet);

      return { flatApplications: flatApps, programInstances, availableYears };
    } catch (error) {
      console.error('Failed to fetch benefit history:', error);
      return { flatApplications: [], programInstances: [], availableYears: [] };
    }
  }
}
